import apiClient from '../../../services/apiClient';

// Thin wrappers over the interactive-content API (base /api/v1). All calls are
// best-effort from the widget's perspective: callers should handle rejection and
// fall back to local behaviour so a widget never hard-crashes when offline or
// when the route isn't deployed yet.

const BASE = '/api/v1';

// ── Widgets ───────────────────────────────────────────────────────────────────
// GET /lessons/:id/widgets → { widgets: [{ id, type, config, index, questions }] }
// (the route wraps the list in a `widgets` key; quiz `questions` come WITHOUT the
// correct answer — grading is server-side only via the attempt endpoint).
export interface WidgetQuestionDto {
  id: string;
  kind: 'mcq' | 'numeric' | 'short';
  prompt: string;
  choices: Array<{ id: string; text: string }> | string[];
  points?: number;
}

export interface WidgetDto {
  id: string;
  type: string;
  config: Record<string, unknown>;
  index: number;
  questions?: WidgetQuestionDto[];
}

export async function fetchLessonWidgets(lessonId: string): Promise<WidgetDto[]> {
  const { data } = await apiClient.get<{ widgets: WidgetDto[] } | WidgetDto[]>(
    `${BASE}/lessons/${lessonId}/widgets`,
  );
  // Tolerate both the documented bare-array shape and the route's { widgets } wrapper.
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.widgets) ? data.widgets : [];
}

// ── Quiz grading ──────────────────────────────────────────────────────────────
// POST /quiz/:questionId/attempt { answer } → { correct, score, explanation }
export interface QuizAttemptResult {
  // null for short answers (stored but not auto-graded).
  correct: boolean | null;
  score: number;
  explanation?: string | null;
  attemptId?: string;
}

export async function submitQuizAttempt(
  questionId: string,
  answer: unknown,
): Promise<QuizAttemptResult> {
  const { data } = await apiClient.post<QuizAttemptResult>(
    `${BASE}/quiz/${questionId}/attempt`,
    { answer },
  );
  return data;
}
